import { Component, Inject, OnInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SHARED_IMPORTS } from '../../shared/shared-imports';
import { OrderObjectByPipe } from '../../../pipes/order-object-by.pipe';

interface Tenant {
	TenantId: string;
	TenantName: string;
}

interface Unit {
	SerialNumber: string;
	TenantId: string;
}

interface Alarm {
	AlarmMessageLocal: string;
	SerialNumber: string;
	TenantName: string;
	StartedTS: string;
	EndedTS: string | null;
	Severity: number;
}

@Component({
	selector: 'global-alarm',
	standalone: true,
	imports: [
		...SHARED_IMPORTS,
		MatSelectModule,
		MatInputModule,
		MatFormFieldModule,
		OrderObjectByPipe
	],
	templateUrl: 'components/global-admin/global-alarm/global-alarm.component.html',
	styleUrls: ['components/global-admin/global-alarm/global-alarm.component.css'],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class GlobalAlarmComponent implements OnInit {
	public filter = '';
	public filterAlarm = '';
	public optionSelected = false;
	public hasAlarms = true;
	public previousOptionSelectedUnit: Unit | null = null;
	public currentOptionSelectedUnit: Unit | null = null;
	public previousOptionSelectedTenant: string | null = null;
	public currentOptionSelectedTenant: string | null = null;

	public tenants: Tenant[] = [];
	public units: Unit[] = [];
	public unitsBySpecifiedTenant: Unit[] = [];
	public alarmListByTenant: Alarm[] = [];
	public alarmListByUnit: Alarm[] = [];
	public filteredAlarms: Alarm[] = [];
	public selectedUnit: Unit | null = null;
	public selectedTenant: string = '';
	public findTenant: string = '';

	constructor(@Inject(HttpClient) private http: HttpClient) {
		// Load from localStorage
		const savedFilter = localStorage.getItem('globalAlarm.filter');
		if (savedFilter) this.filter = savedFilter;
		
		const savedFilterAlarm = localStorage.getItem('globalAlarm.filterAlarm');
		if (savedFilterAlarm) this.filterAlarm = savedFilterAlarm;
	}

	ngOnInit(): void {
		this.getTenants();
		this.getAllUnits();
	}

	public doFilter(): void {
		this.filter = this.filter.toLowerCase();
		localStorage.setItem('globalAlarm.filter', this.filter);
	}

	public doFilterAlarm(): void {
		this.filterAlarm = this.filterAlarm.toLowerCase();
		localStorage.setItem('globalAlarm.filterAlarm', this.filterAlarm);
	}

	public showRecordAlarm(record: any): boolean {
		return this.filterMatchAlarm(record);
	}

	private filterMatchAlarm(record: any): boolean {
		return record.AlarmMessageLocal &&
			record.AlarmMessageLocal.toLowerCase().includes(this.filterAlarm) &&
			this.upToThreeMonths(record);
	}

	private upToThreeMonths(record: { EndedTS: string | null }): boolean {
		const today = new Date();
		if (record.EndedTS !== null) {
			const end = new Date(record.EndedTS);
			const monthDiff = (today.getFullYear() - end.getFullYear()) * 12 + (today.getMonth() - end.getMonth());
			return monthDiff < 3;
		} else {
			return true;
		}
	}

	public showRecordUnit = (record: any): boolean => {
		return this.filterMatch(record.SerialNumber);
	}

	public filterMatch(str: string): boolean {
		return str && str.toLowerCase().includes(this.filter);
	}

	public getTenants(): void {
		this.http.get<Tenant[]>('/api/Tenant/GetTenants').subscribe(tenants => {
			this.tenants = tenants;
		});
	}

	public filterAlarmsByTenant(tenantId: string): void {
		this.http.get<Alarm[]>('/api/Alarm/GetAlarmsByTenant?tenantId=' + tenantId).subscribe(alarms => {
			this.filteredAlarms = alarms;
			this.alarmListByTenant = alarms;
			this.hasAlarms = this.filteredAlarms.length > 0;
		});
	}

	public filterAlarmsByUnit(): void {
		if (!this.selectedUnit) return;
		this.http.get<Alarm[]>('/api/Alarm/GetAlarmsByUnit?serialNumber=' + this.selectedUnit.SerialNumber).subscribe(alarms => {
			this.filteredAlarms = alarms;
			this.alarmListByUnit = alarms;
			this.hasAlarms = this.filteredAlarms.length > 0;
		});
	}

	public toggleSelectTenant(): void {
		this.findTenant = this.selectedTenant;
		// Clear unit selection
		this.selectedUnit = null;
		this.loadAlarmsForTenant();
	}

	public toggleSelectUnit(): void {
		if (!this.selectedUnit) return;
		
		this.findTenant = this.selectedUnit.TenantId;
		if (this.currentOptionSelectedUnit === this.selectedUnit) {
			if (this.optionSelected) {
				this.selectedUnit = null;
				this.loadAlarmsForTenant();
				this.hasAlarms = true;
				this.optionSelected = false;
				this.previousOptionSelectedUnit = this.currentOptionSelectedUnit;
			} else {
				this.loadAlarmsForUnit();
				this.optionSelected = true;
			}
			this.currentOptionSelectedUnit = this.selectedUnit;
		} else {
			this.currentOptionSelectedUnit = this.selectedUnit;
			this.optionSelected = true;
			this.loadAlarmsForUnit();
		}
	}

	public getAllUnits(): void {
		this.http.get<Unit[]>('/api/Unit/GetAllUnitsWithMacAddress').subscribe(units => {
			this.units = units;
			this.unitsBySpecifiedTenant = units;
		});
	}

	public loadAlarmsForTenant(): void {
		if (this.alarmListByTenant !== undefined) {
			this.filteredAlarms = this.alarmListByTenant;
			this.hasAlarms = this.filteredAlarms.length > 0;
		}
	}

	public loadAlarmsForUnit(): void {
		this.filteredAlarms = this.alarmListByUnit;
		this.hasAlarms = this.filteredAlarms.length > 0;
	}

	public updateUnitList(): void {
		this.http.get<Unit[]>('/api/Unit/GetUnitsBySpecifiedTenant?tenantId=' + this.selectedTenant).subscribe(units => {
			this.unitsBySpecifiedTenant = units;
		});
		this.filterAlarmsByTenant(this.selectedTenant);
		this.findTenant = this.selectedTenant;
	}

	public calculateDuration(startDate: string, endDate: string | null): string {
		if (!endDate) return "Not ended";

		const start = new Date(startDate);
		const end = new Date(endDate);
		let diff = (end.getTime() - start.getTime()) / 1000;

		const seconds = Math.floor(diff % 60); diff /= 60;
		const minutes = Math.floor(diff % 60); diff /= 60;
		const hours = Math.floor(diff % 24); diff /= 24;
		const days = Math.floor(diff % 30.5); diff /= 30.5;
		const months = Math.floor(diff % 12);
		const years = Math.floor(diff / 12);

		const parts = [
			years > 0 ? `${years}y` : '',
			months > 0 ? `${months}mon` : '',
			days > 0 ? `${days}d` : '',
			hours > 0 ? `${hours}h` : '',
			minutes > 0 ? `${minutes}m` : '',
			seconds > 0 ? `${seconds}s` : ''
		].filter(Boolean);

		return parts.length ? parts.join(' ') : '0s';
	}

}
